<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipTypeList } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { getSeniorReinforceGoldPrice, getSeniorReinforceStonePrice } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'
import type { UserAttrType } from '@/types/user'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (item.reinforcedLevel < 15) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

function handleChoice(item: EquipItemType | undefined) {
  if (item && item?.reinforcedLevel < 15) return ElMessage.warning('高阶强化需放入至少+15的装备')
  equipItemId.value = item?.id || ''
}

const reinforceResShow = ref(false)
function handleReinforce() {
  if (!equipInfo.value) return
  const goldPrice = getSeniorReinforceGoldPrice(equipInfo.value.reinforcedLevel)
  if (goldPrice > userStore.gold) {
    return ElMessage.warning('您的金币不足')
  }
  const stonePrice = getSeniorReinforceStonePrice(equipInfo.value.reinforcedLevel)
  if (stonePrice > userStore.consumables.forgeStone) {
    return ElMessage.warning('您的锻造石不足')
  }
  const { id, quality, name } = equipInfo.value
  ElMessageBox.confirm(
    `<p class="affix">确定强化<span class="${quality}">${name}</span>吗？【本次强化消耗 ${stonePrice}<span class="wild">锻造石</span>】</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(reinforce)
    .catch(() => {})

  async function reinforce() {
    const userAttr = await socketEmitAsync<UserAttrType>({ type: 'seniorReinforce', data: { id }, limit: false })
    userStore.userAttr = userAttr

    const item = userStore.itemList.find((item) => item.id === id)
    if (!item || !equipInfo.value) return
    userStore.gold -= goldPrice
    userStore.consumables.forgeStone -= stonePrice
    item.reinforcedLevel++

    reinforceResShow.value = true
  }
}
</script>

<template>
  <div class="senior-reinforce">
    <div class="senior-reinforce-table border-wrap">
      <h4>锻造台</h4>
      <div class="senior-reinforce-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>高阶强化说明</h4>
            <p>1. 在装备强化等级达到<span class="info">15</span>级以上时，可进行高阶强化。</p>
            <p>2. 高阶强化需消耗 <span class="gold">金币</span>及 <span class="wild">锻造石</span>。</p>
            <p>3. 高阶强化<span class="grow">必定成功</span>，强化后每级提升<span class="info">0.2% 全伤害加成</span>。</p>
            <p>4. 当前版本最高高阶强化至 +18</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <el-row>
            <el-col :span="12">
              <p>强化等级：{{ equipInfo.reinforcedLevel }}</p>
            </el-col>
            <el-col :span="12">
              <p>强化成功率：100%</p>
            </el-col>
          </el-row>
          <template v-if="equipInfo.reinforcedLevel < 18">
            <p>强化花费：{{ formatNumberWithCommas(getSeniorReinforceGoldPrice(equipInfo.reinforcedLevel)) }} 金币</p>
            <p style="text-indent: 5em">{{ getSeniorReinforceStonePrice(equipInfo.reinforcedLevel) }} 锻造石</p>
          </template>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleReinforce" :disabled="equipInfo.reinforcedLevel === 18">
            {{ equipInfo.reinforcedLevel === 18 ? '已强化满级' : '开始强化' }}
          </el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>背包</h4>
      <p class="cur-gold">
        <span style="margin-right: 10px">💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
        <span>🟠 {{ formatNumberWithCommas(userStore.consumables.forgeStone) }}</span>
      </p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoice(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
        <p class="affix" style="text-align: center" v-show="!itemList.length">您的背包中不存在<span class="info">+15 及以上</span>的装备</p>
      </div>
    </div>

    <el-dialog v-model="reinforceResShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">高阶强化成功</span>
      </h2>
      <template v-if="equipInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <div style="width: 50%; margin: 0 auto">
              <CommonBtn size="small" style="width: 100%">
                <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
              </CommonBtn>
            </div>
          </template>
          <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 60vh" />
        </el-popover>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="reinforceResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.senior-reinforce {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .senior-reinforce-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .senior-reinforce-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
